import { useState } from 'react'

import { admin } from '../lib/api/admin.ts'
import type { DetectionStats, VolumeStats } from '../lib/api/admin.ts'
import { formatInt } from '../lib/format.ts'
import { useMetricsStream } from '../lib/sse/useMetricsStream.ts'
import { AlertRail } from '../components/panels/AlertRail.tsx'
import { FleetHealthPanel } from '../components/panels/FleetHealthPanel.tsx'
import { FleetOpsPanel } from '../components/panels/FleetOpsPanel.tsx'
import { IngestPanel } from '../components/panels/IngestPanel.tsx'
import { LatencyPanel } from '../components/panels/LatencyPanel.tsx'
import { RejectionsPanel } from '../components/panels/RejectionsPanel.tsx'
import { RuntimePanel } from '../components/panels/RuntimePanel.tsx'
import { TenantsPanel } from '../components/panels/TenantsPanel.tsx'
import { ThroughputPanel } from '../components/panels/ThroughputPanel.tsx'
import { TotalsPanel } from '../components/panels/TotalsPanel.tsx'
import { DetectionStatsPanel } from '../components/stats/DetectionStatsPanel.tsx'
import { TenantVolumeDrawer } from '../components/stats/TenantVolumeDrawer.tsx'
import { VolumeStatsPanel } from '../components/stats/VolumeStatsPanel.tsx'
import { PageHeader } from '../components/layout/PageHeader.tsx'
import { ConnectionBadge, StaleBanner } from '../components/primitives/ConnectionStatus.tsx'
import { ServerClock } from '../components/primitives/RelativeTime.tsx'

// Estáveis (módulo): o painel refaz a busca quando a função muda.
const loadDetectionStats = (range: { from: string; to: string }, signal: AbortSignal): Promise<DetectionStats> =>
  admin.detectionStats(range, signal)
const loadVolumeStats = (signal: AbortSignal): Promise<VolumeStats> => admin.volumeStats({}, signal)

/**
 * The console.
 *
 * Layout carries the hierarchy: fleet health is the widest panel on the top row
 * because a killed agent is the finding this screen exists to surface, ingestion
 * sits beside it because queue depth is the earliest warning the platform has,
 * and everything below is context. Panels are separated by the grid's 1px gap
 * over a line-coloured background rather than by borders of their own — one
 * hairline between two panels instead of two.
 */
export function Dashboard() {
  const stream = useMetricsStream()
  const { metrics, fleet, throughput, alerts, connection, diagnostics } = stream
  // Detalhe de uma empresa (volume por agente), aberto a partir dos painéis de estatística.
  const [openTenant, setOpenTenant] = useState<{ id: string; name: string | null } | null>(null)
  const open = (id: string, name: string | null): void => setOpenTenant({ id, name })

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-canvas">
      <PageHeader
        title="Console"
        actions={
          <>
            <span className="text-micro text-fg-muted">
              hora do servidor <ServerClock iso={stream.serverTime} />
            </span>
            <ConnectionBadge state={connection} lastEventAt={stream.lastEventAt} />
          </>
        }
      />

      <StaleBanner
        state={connection}
        lastEventAt={stream.lastEventAt}
        serverTime={stream.serverTime}
      />

      {/* Sem o papel, nenhum dado vai chegar — nunca. Uma grade inteira de
          travessões sob o banner deixaria o operador esperando por algo que não
          vem; o painel se cala e diz o que fazer. */}
      {connection === 'forbidden' ? (
        <main className="flex flex-1 flex-col items-start gap-2 p-6">
          <h2 className="text-base font-medium text-fg">Sem permissão nesta área.</h2>
          <p className="max-w-prose text-sm leading-snug text-fg-secondary">
            Exclusivo da equipe da Black Ghost.
          </p>
        </main>
      ) : (
      <main className="grid flex-1 grid-cols-1 gap-px bg-line-subtle xl:grid-cols-12">
        <div className="grid grid-cols-1 gap-px bg-line-subtle xl:col-span-9 xl:grid-cols-12">
          {/* Uma faixa da largura toda acima da primeira linha: o total acumulado
              e o do dia são os únicos números desta tela que respondem "quanto
              esta plataforma já processou" — todo o resto aqui é taxa instantânea
              ou janela. A grade só ganha uma linha; o rail de alertas à direita
              segue com a altura inteira. */}
          <TotalsPanel metrics={metrics} className="xl:col-span-12" />

          <FleetHealthPanel
            fleet={fleet}
            lastFleetAt={stream.lastFleetAt}
            className="min-h-104 xl:col-span-7"
          />
          <IngestPanel metrics={metrics} className="xl:col-span-5" />

          <ThroughputPanel points={throughput} className="min-h-48 xl:col-span-12" />

          <TenantsPanel
            fleet={fleet}
            lastFleetAt={stream.lastFleetAt}
            className="min-h-72 xl:col-span-7"
          />
          <RejectionsPanel
            rejections={metrics?.rejections_last_hour ?? []}
            rejectedPerSec={metrics?.ingest.events_per_sec_rejected ?? null}
            className="xl:col-span-5"
          />

          <LatencyPanel latency={metrics?.latency_ms ?? null} className="xl:col-span-5" />
          <FleetOpsPanel
            fleet={fleet}
            lastFleetAt={stream.lastFleetAt}
            className="min-h-64 xl:col-span-4"
          />
          <RuntimePanel
            runtime={metrics?.runtime ?? null}
            ingest={metrics?.ingest ?? null}
            sampleKey={metrics?.server_time ?? null}
            resetKey={diagnostics.snapshots}
            className="xl:col-span-3"
          />

          {/* Estatísticas de banco, fora do stream: poll de 60 s (§4.7, §4.8). */}
          <DetectionStatsPanel
            load={loadDetectionStats}
            showTenants
            onOpenTenant={open}
            className="min-h-64 xl:col-span-7"
          />
          <VolumeStatsPanel load={loadVolumeStats} onOpenTenant={open} className="xl:col-span-5" />
        </div>

        <AlertRail alerts={alerts} className="xl:col-span-3" />
      </main>
      )}

      {openTenant !== null && (
        <TenantVolumeDrawer
          tenantId={openTenant.id}
          tenantName={openTenant.name}
          onClose={() => setOpenTenant(null)}
        />
      )}

      {/* Diagnostics, not decoration: the retained-points figure is what makes
          "the series does not grow" something an operator can check, not a claim. */}
      <footer className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-0.5 border-t border-line px-3 py-1 text-micro text-fg-muted">
        <span>
          pontos retidos <span className="tnum">{formatInt(diagnostics.seriesLength)}</span>
        </span>
        <span>
          descartados <span className="tnum">{formatInt(diagnostics.pointsDropped)}</span>
        </span>
        <span>
          snapshots <span className="tnum">{formatInt(diagnostics.snapshots)}</span>
        </span>
        <span>
          reconexões <span className="tnum">{formatInt(diagnostics.reconnects)}</span>
        </span>
        <span className={diagnostics.malformed > 0 ? 'text-warn' : undefined}>
          payloads rejeitados <span className="tnum">{formatInt(diagnostics.malformed)}</span>
        </span>
        <span className="ml-auto">alertas em buffer <span className="tnum">{formatInt(alerts.length)}</span></span>
      </footer>
    </div>
  )
}
